import React from 'react';
import styled from 'styled-components';
import { system } from './system';

// Molekule Label: display block, transition 250ms, margin 0 0 4px 4px, font-size from theme.
// Supports isFloatable/isFloating/isFocused/isDisabled for use inside Input.
// External call sites use: <Label htmlFor={name} disabled={disabled}>{children}</Label>

interface LabelSys {
  [key: string]: any;
}

interface LabelInternalProps {
  $isFloatable?: boolean;
  $isFloating?: boolean;
  $isFocused?: boolean;
  $isDisabled?: boolean;
  $multiline?: boolean;
  $sys?: LabelSys;
}

const StyledLabel = styled.label<LabelInternalProps>`
  display: block;
  transition: 250ms;
  margin: 0 0 4px 4px;
  font-size: ${(p: any) =>
    p.theme && p.theme.typography
      ? `${p.theme.typography.fontSize}px`
      : '16px'};

  ${(p: LabelInternalProps & { theme?: any }) =>
    p.$isFloatable
      ? `
        position: absolute;
        margin: 0;
        line-height: 14px;
        left: 9px;
        font-size: ${p.$isFloating ? '12px' : '16px'};
        font-weight: 500;
        top: ${p.$isFloating ? '6px' : '50%'};
        transform: ${p.$isFloating ? 'none' : 'translateY(-50%)'};
        color: ${p.theme && p.theme.colors ? p.theme.colors.greyDarker : '#767980'};
        user-select: none;
        z-index: ${p.$isFloating ? '1' : 'auto'};
        &:hover { cursor: text; }
      `
      : ''}

  ${(p: LabelInternalProps & { theme?: any }) =>
    p.$isFocused && p.theme && p.theme.colors
      ? `color: ${p.theme.colors.primary};`
      : ''}

  ${(p: LabelInternalProps & { theme?: any }) =>
    p.$isDisabled && p.theme && p.theme.colors
      ? `color: ${p.theme.colors.grey};`
      : ''}

  ${(p: any) => system(p.$sys, p.theme)}
`;

export interface LabelProps {
  htmlFor?: string;
  disabled?: boolean;
  isFloatable?: boolean;
  isFloating?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  multiline?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLLabelElement>;
  [key: string]: any;
}

const Label: React.FC<LabelProps> = ({
  htmlFor,
  disabled,
  isFloatable,
  isFloating,
  isFocused,
  isDisabled,
  multiline,
  children,
  className,
  style,
  onClick,
  ...rest
}) => {
  const { styles, $sys, ...domRest } = rest as any;
  return (
    <StyledLabel
      htmlFor={htmlFor}
      $isFloatable={isFloatable}
      $isFloating={isFloating}
      $isFocused={isFocused}
      $isDisabled={isDisabled || disabled}
      $multiline={multiline}
      className={className}
      style={style}
      onClick={onClick}
      $sys={$sys}
      {...domRest}
    >
      {children}
    </StyledLabel>
  );
};

export default Label;
